import { toast } from 'vue-sonner'

/**
 * This behavior is like import type
 * @typedef {import('vue-sonner').ExternalToast} ExternalToast
 */

export function useNotification() {
  const config = {
    duration: 4000,
  }

  /**
   *
   * @param {string} message
   * @param {ExternalToast} option
   * @returns {void}
   */
  const success = (message, option) =>
    toast.success(message, {
      ...config,
      ...option,
    })

  /**
   *
   * @param {string} message
   * @param {ExternalToast} option
   * @returns {void}
   */
  const error = (message, option) =>
    toast.error(message, {
      ...config,
      duration: 6000,
      ...option,
    })

  /**
   *
   * @param {string} message
   * @param {ExternalToast} option
   * @returns {void}
   */
  const info = (message, option) =>
    toast.info(message, {
      ...config,
      ...option,
    })

  /**
   *
   * @param {string} message
   * @param {ExternalToast} option
   * @returns {void}
   */
  const warning = (message, option) =>
    toast.warning(message, {
      ...config,
      ...option,
    })

  /**
   * Handles asynchronous processes with a reactive toast notification.
   * Automatically transitions the toast state from 'loading' to 'success' or 'error'
   * based on the provided Promise resolution.
   *
   * @param {Promise|Function} apiCall - A Promise or a function that returns a Promise to be tracked.
   * @param {Object} options - Configuration object for the toast states.
   * @param {string|VNode|Renderable} options.loading - Message or component to display during the pending state.
   * @param {string|Function} options.success - Message or a function that receives the resolved data and returns a message/component.
   * @param {string|Function} options.error - Message or a function that receives the rejected error and returns a message/component.
   * @returns {string|number} The unique identifier of the toast.
   * * @example
   * promise(axios.post('/api/save'), {
   * loading: 'Saving changes...',
   * success: (data) => `Successfully saved ${data.name}`,
   * error: (err) => `Update failed: ${err.message}`
   * });
   */
  const promise = (apiCall, { loading, success, error }) => {
    return toast.promise(apiCall, {
      loading: loading || 'Sedang memproses...',
      success: data => success(data),
      error: err => error(err),
    })
  }

  return {
    success,
    error,
    info,
    warning,
    promise,
  }
}
